import type { ReactNode } from "react";
import { Info, AlertTriangle, Lightbulb, StickyNote } from "lucide-react";

type CalloutType = "info" | "warning" | "tip" | "note";

interface CalloutProps {
  type?: CalloutType;
  children: ReactNode;
}

const TYPE_META: Record<
  CalloutType,
  { color: string; tint: string; icon: typeof Info; label: string }
> = {
  info: { color: "var(--lavender-400)", tint: "rgba(156,139,194,0.10)", icon: Info, label: "Info" },
  warning: { color: "#F2B057", tint: "rgba(242,176,87,0.10)", icon: AlertTriangle, label: "Warning" },
  tip: { color: "#9BC289", tint: "rgba(155,194,137,0.10)", icon: Lightbulb, label: "Tip" },
  note: { color: "var(--dyna-fg-muted)", tint: "rgba(202,187,220,0.06)", icon: StickyNote, label: "Note" },
};

export function Callout({ type = "info", children }: CalloutProps) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  return (
    <div
      role="note"
      aria-label={meta.label}
      style={{
        margin: "var(--s-md) 0",
        padding: "16px 20px",
        borderInlineStart: `4px solid ${meta.color}`,
        background: meta.tint,
        borderRadius: 12,
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        fontFamily: "Geist, system-ui, sans-serif",
        fontSize: 15,
        lineHeight: 1.6,
        color: "var(--dyna-fg)",
      }}
    >
      <Icon size={18} color={meta.color} style={{ flexShrink: 0, marginTop: 3 }} />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}
